import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import {
	type StatusBarItem,
	useWorkspacePage,
} from "@/components/workspace";
import { Plus, Search } from "lucide-react";
import type { RecurringEntryTemplate } from "@/gen/omnios/v1/accounting_pb";
import {
	useCreateRecurringEntry,
} from "@/hooks/use-accounting";
import { AG_GRID_LOCALE_RU } from "@/lib/ag-grid-locale-ru";
import { formatDate } from "@/lib/format";
import { useBreadcrumbs } from "@/lib/use-breadcrumbs";
import { useGridLayout } from "@/lib/use-grid-layout";
import { useServerGrid } from "@/lib/use-server-grid";
import { accountingClient } from "@/lib/accounting-client";

import type { ColDef, ValueFormatterParams } from "@ag-grid-community/core";
import { AgGridReact } from "@ag-grid-community/react";
import "@ag-grid-community/styles/ag-grid.css";
import "@ag-grid-community/styles/ag-theme-quartz.css";

import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

export const Route = createFileRoute("/accounting/recurring")({
	component: RecurringEntriesPage,
});

/** Human-readable cron descriptions */
const CRON_PRESETS: { label: string; cron: string }[] = [
	{ label: "Ежемесячно 1-го числа", cron: "0 0 1 * *" },
	{ label: "Ежемесячно 15-го числа", cron: "0 0 15 * *" },
	{ label: "Ежемесячно последний день", cron: "0 0 L * *" },
	{ label: "Еженедельно (понедельник)", cron: "0 0 * * 1" },
	{ label: "Ежеквартально", cron: "0 0 1 */3 *" },
	{ label: "Ежегодно 1 января", cron: "0 0 1 1 *" },
];

function cronToLabel(cron: string): string {
	return CRON_PRESETS.find((p) => p.cron === cron)?.label ?? cron;
}

function RecurringEntriesPage() {
	const { t } = useTranslation("accounting");
	const { onGridReady, onStateChanged } = useGridLayout("recurring");
	const createMutation = useCreateRecurringEntry();

	useBreadcrumbs([
		{
			label: t("recurring_entries", {
				defaultValue: "Повторяющиеся проводки",
			}),
		},
	]);

	const [createOpen, setCreateOpen] = useState(false);

	const { gridRef, gridProps, totalRows, refreshGrid } = useServerGrid<RecurringEntryTemplate>({
		colToField: {
			lastRun: "last_run",
			nextRun: "next_run",
			cronExpression: "cron_expression",
		},
		fetchRows: async ({ pageSize, pageToken }) => {
			const resp = await accountingClient.listRecurringEntries({
				page: { pageSize, pageToken },
			});
			return {
				rows: resp.templates,
				nextPageToken: resp.page?.nextPageToken,
				totalCount: resp.page?.totalCount ?? 0,
			};
		},
	});

	const columnDefs = useMemo<ColDef<RecurringEntryTemplate>[]>(
		() => [
			{
				field: "name",
				headerName: t("template_name", { defaultValue: "Название" }),
				flex: 1,
				minWidth: 200,
				cellClass: "font-medium",
			},
			{
				field: "cronExpression",
				headerName: t("schedule", { defaultValue: "Расписание" }),
				width: 200,
				valueFormatter: (p: ValueFormatterParams<RecurringEntryTemplate>) =>
					cronToLabel(p.value ?? ""),
			},
			{
				field: "active",
				headerName: t("status_active", { defaultValue: "Активен" }),
				width: 90,
				filter: false,
				cellRenderer: (p: { value: boolean; data: RecurringEntryTemplate }) => {
					return <span style={{ color: p.value ? "hsl(var(--chart-2))" : "hsl(var(--muted-foreground))" }}>{p.value ? "Да" : "Нет"}</span>;
				},
			},
			{
				field: "description",
				headerName: t("entry_description"),
				width: 200,
			},
			{
				field: "lastRun",
				headerName: t("last_run", { defaultValue: "Последний запуск" }),
				width: 140,
				filter: "agDateColumnFilter",
				valueFormatter: (p: ValueFormatterParams<RecurringEntryTemplate>) => {
					const d = p.data?.lastRun;
					return d ? formatDate(d.toDate(), "dd.MM.yyyy HH:mm") : "—";
				},
			},
			{
				field: "nextRun",
				headerName: t("next_run", { defaultValue: "Следующий" }),
				width: 140,
				filter: "agDateColumnFilter",
				valueFormatter: (p: ValueFormatterParams<RecurringEntryTemplate>) => {
					const d = p.data?.nextRun;
					return d ? formatDate(d.toDate(), "dd.MM.yyyy HH:mm") : "—";
				},
			},
			{
				field: "lines",
				headerName: t("lines_count", { defaultValue: "Строк" }),
				width: 70,
				filter: false,
				valueGetter: (p) => p.data?.lines?.length ?? 0,
			},
		],
		[t],
	);

	const defaultColDef = useMemo<ColDef>(
		() => ({
			sortable: true,
			resizable: true,
			filter: true,
			filterParams: { buttons: ["apply", "reset"], closeOnApply: true },
		}),
		[],
	);

	const statusBarItems = useMemo<StatusBarItem[]>(
		() => [
			{
				id: "count",
				content: t("row_count", { count: totalRows }),
				align: "left" as const,
			},
		],
		[totalRows, t],
	);

	useWorkspacePage({ statusBar: statusBarItems });

	return (
		<div className="flex flex-col h-full overflow-hidden">
			<div className="flex items-center gap-2 px-4 py-2 shrink-0">
				<div className="relative flex-1">
					<Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
					<Input
						placeholder={t("search_placeholder", { defaultValue: "Поиск..." })}
						onChange={(e) =>
							gridRef.current?.api?.setGridOption(
								"quickFilterText",
								e.target.value,
							)
						}
						className="pl-9 h-9"
					/>
				</div>
				<Button
					variant="outline"
					size="sm"
					className="h-9"
					onClick={() => gridRef.current?.api?.exportDataAsCsv()}
				>
					CSV
				</Button>
				<Button size="sm" className="h-9" onClick={() => setCreateOpen(true)}>
					<Plus className="h-4 w-4 mr-1" />
					{t("recurring_create", { defaultValue: "Создать расписание" })}
				</Button>
			</div>
			<div className="flex-1 ag-theme-quartz" style={{ width: "100%" }}>
				<AgGridReact<RecurringEntryTemplate>
					ref={gridRef}
					{...gridProps}
					columnDefs={columnDefs}
					defaultColDef={defaultColDef}
					localeText={AG_GRID_LOCALE_RU}
					onGridReady={onGridReady}
					onSortChanged={() => {
						onStateChanged();
						refreshGrid();
					}}
					onFilterChanged={() => { onStateChanged(); refreshGrid(); }}
					onColumnMoved={onStateChanged}
					onColumnResized={onStateChanged}
					onColumnVisible={onStateChanged}
					rowHeight={28}
					headerHeight={32}
					animateRows={false}
					getRowId={(p) => p.data.id}
					overlayNoRowsTemplate={`<div style="text-align:center;padding:40px"><p style="font-size:13px;font-weight:500">${t("empty_recurring_title", { defaultValue: "Нет повторяющихся проводок" })}</p><p style="font-size:11px;color:hsl(var(--muted-foreground));margin-top:4px">${t("empty_recurring_desc", { defaultValue: "Создайте расписание для автоматического проведения" })}</p></div>`}
				/>
			</div>

			<CreateRecurringDialog
				open={createOpen}
				onOpenChange={setCreateOpen}
				onCreate={(values) => {
					createMutation.mutate(values, {
						onSuccess: () => {
							setCreateOpen(false);
							refreshGrid();
							toast.success(t("recurring_created", { defaultValue: "Расписание создано" }));
						},
					});
				}}
				isPending={createMutation.isPending}
			/>
		</div>
	);
}

function CreateRecurringDialog({
	open,
	onOpenChange,
	onCreate,
	isPending,
}: {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	onCreate: (values: {
		name: string;
		cronExpression: string;
		description: string;
		currency: string;
		basis: string;
	}) => void;
	isPending: boolean;
}) {
	const { t } = useTranslation("accounting");
	const [name, setName] = useState("");
	const [cronExpression, setCronExpression] = useState(CRON_PRESETS[0].cron);
	const [description, setDescription] = useState("");

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent>
				<DialogHeader>
					<DialogTitle>
						{t("recurring_create", { defaultValue: "Создать повторяющуюся проводку" })}
					</DialogTitle>
				</DialogHeader>
				<div className="space-y-4 py-2">
					<div className="space-y-2">
						<Label>{t("template_name", { defaultValue: "Название" })}</Label>
						<Input
							value={name}
							onChange={(e) => setName(e.target.value)}
							placeholder={t("recurring_name_placeholder", { defaultValue: "Аренда офиса" })}
						/>
					</div>
					<div className="space-y-2">
						<Label>{t("schedule", { defaultValue: "Расписание" })}</Label>
						<Select value={cronExpression} onValueChange={setCronExpression}>
							<SelectTrigger>
								<SelectValue />
							</SelectTrigger>
							<SelectContent>
								{CRON_PRESETS.map((preset) => (
									<SelectItem key={preset.cron} value={preset.cron}>
										{preset.label}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</div>
					<div className="space-y-2">
						<Label>{t("entry_description")}</Label>
						<Input
							value={description}
							onChange={(e) => setDescription(e.target.value)}
						/>
					</div>
				</div>
				<DialogFooter>
					<Button variant="outline" onClick={() => onOpenChange(false)}>
						{t("cancel", { ns: "common" })}
					</Button>
					<Button
						onClick={() =>
							onCreate({
								name,
								cronExpression,
								description,
								currency: "RUB",
								basis: "accrual",
							})
						}
						disabled={isPending || !name.trim()}
					>
						{t("create", { ns: "common" })}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}
